import { purchaseService } from '#/client/api';
import { PackageDefinition, ProductsResponse } from '#/client/models';
import { ShowPaywallParams } from '#/plugins/paywall/models';

export interface ProductInfo {
  name: string;
  description?: string;
  productIdentifier: string;
  grantedEntitlementTags: readonly string[];
  groupId?: string;
}

const debugLog = (message: string) =>
  console.debug(`Purchasable products: ${message}`);

interface GroupOrTierOrPlan {
  purchasable: boolean;
  name: string;
}

interface FilterResult {
  result: boolean;
  // what to log if this filter removes the product
  message: string;
  // what to do after this filter removes the product
  after?: 'setOtherPlatformErrorGroupId';
}

type FilterFunction = (product: ProductInfo) => FilterResult;

const isPurchasable = (item: GroupOrTierOrPlan) => {
  if (!item.purchasable) {
    debugLog(`Filter 0: Not purchasable: "${item.name}"`);

    return false;
  }

  return true;
};

export const mapPackageDefinitionToProductInfoList = (
  packageDefinition: PackageDefinition,
  transformProductIdentifierSafely: (id: string) => string
): ProductInfo[] => {
  const { oneTimePurchases = [], subscriptionGroups = [] } = packageDefinition;

  return oneTimePurchases
    .map((item) => ({
      name: item.name,
      description: item.description,
      productIdentifier: transformProductIdentifierSafely(
        item.web?.productIdentifier ?? ''
      ),
      grantedEntitlementTags: item.grantedEntitlementTags ?? [],
      groupId: '',
    }))
    .concat(
      subscriptionGroups.filter(isPurchasable).flatMap((group) =>
        group.tiers
          .filter(isPurchasable)
          .flatMap((tier) => tier.plans)
          .filter(isPurchasable)
          .map((plan) => ({
            name: plan.name,
            description: plan.description,
            productIdentifier: transformProductIdentifierSafely(
              plan.web?.productIdentifier ?? ''
            ),
            grantedEntitlementTags: plan.grantedEntitlementTags ?? [],
            groupId: group.id,
          }))
      )
    );
};

const doesntGrantTagsNeededForVideo = (
  product: ProductInfo,
  fetchWithEntitlementTags: string[]
): FilterResult => ({
  result:
    // the tags required for a video were passed in
    Boolean(fetchWithEntitlementTags.length) &&
    // none of the granted tags are included in the needed tags
    !product.grantedEntitlementTags.some((tag) =>
      fetchWithEntitlementTags.includes(tag)
    ),
  message: `Filter 1: Doesn't grant tags needed for video: "${product.name}"`,
});

const doesntGrantNewTagsForUser = (
  product: ProductInfo,
  filterOutMatchingEntitlements: string[]
): FilterResult => ({
  result:
    // the user's current tags were passed in
    Boolean(filterOutMatchingEntitlements.length) &&
    // all the granted tags are already included in the user's current tags
    product.grantedEntitlementTags.every((tag) =>
      filterOutMatchingEntitlements.includes(tag)
    ),
  message: `Filter 2: Doesn't grant new tags for user: "${product.name}"`,
});

const isOutsideOfSubscriptionGroups = (
  product: ProductInfo,
  subscriptionGroups: string[]
): FilterResult => ({
  result:
    // the current subscription groups were passed in
    Boolean(subscriptionGroups.length) &&
    // the product is not a subscription OR
    (!product.groupId ||
      // the product's group is not included in the requested groups
      !subscriptionGroups.includes(product.groupId)),
  message: `Filter 3: Outside of subscription groups: "${product.name}"`,
});

const isFilteredByCore = (
  product: ProductInfo,
  prefilteredProductIdentifiers: string[]
): FilterResult => ({
  result: !prefilteredProductIdentifiers.includes(product.productIdentifier),
  message: `Filter 4: Filtered by Core: "${product.name}"`,
});

const isAlreadyOwnedProduct = (
  product: ProductInfo,
  currentUserProductIdentifiers: string[]
): FilterResult => ({
  result: currentUserProductIdentifiers.includes(product.productIdentifier),
  message: `Filter 5: Already owned product: "${product.name}"`,
});

const isAlreadyOwnedGroup = (
  product: ProductInfo,
  filterOutSubscriptionGroupIds: string[]
): FilterResult => ({
  result:
    // the product has a group ID
    !!product.groupId &&
    // that group ID is already owned (or to be filtered out for another reason)
    filterOutSubscriptionGroupIds.includes(product.groupId),
  message: `Filter 6: Already owned group: "${product.name}"`,
});

const isGroupOwnedOnOtherPlatform = (
  product: ProductInfo,
  otherPlatformSubscriptionGroupIds: string[]
): FilterResult => ({
  result:
    // the product has a group ID
    !!product.groupId &&
    // that group ID is already owned on another platform
    otherPlatformSubscriptionGroupIds.includes(product.groupId),
  message: `Filter 7: Group owned on other platform: "${product.name}"`,
  after: 'setOtherPlatformErrorGroupId',
});

interface Result extends FilteredProductResult {
  productsResponse: ProductsResponse;
  otherPlatformErrorGroupId?: string;
}

interface FilteredProductResult {
  productIdentifiers: string[];
  productInfoMap: Record<string, ProductInfo>;
}

/**
 * Helper function for fetching purchasable product identifiers.
 * Don't use directly. Use `getPurchasableProducts` in the paywall store instead.
 */
export const getPurchasableProductIdentifiers = async (
  params: ShowPaywallParams,
  currentUserProductIdentifiers: string[],
  otherPlatformGroupIds: string[],
  transformProductIdentifierSafely: (id: string) => string = (id: string) => id
): Promise<Result> => {
  // fetch package definitions from Core
  const productsResponse = await purchaseService
    .getProducts(params.fetchWithEntitlementTags)
    .then((res) => res.data);
  const { packageDefinition, products } = productsResponse;

  // prepare a result object, so we can early return with empty ids, or update the ids at the end
  const functionResult: Result = {
    productIdentifiers: [],
    productInfoMap: {},
    productsResponse,
  };

  if (!packageDefinition || !products) {
    debugLog('No package definition or products array.');

    return functionResult;
  }

  // map the useful info from the package definition structure to a simple list, excluding non-purchasable items
  const productInfos = mapPackageDefinitionToProductInfoList(
    packageDefinition,
    transformProductIdentifierSafely
  );

  if (!productInfos.length) {
    debugLog('No purchasable products in the package definition.');

    return functionResult;
  }

  // unlike the package definition, the products array might be pre-filtered on the server side, and we must request the integration to filter additionally if necessary (e.g. Cleeng localization IDs).
  const prefilteredProductIdentifiers = products.map((product) =>
    transformProductIdentifierSafely(product.id)
  );

  const {
    fetchWithEntitlementTags = [],
    filterOutMatchingEntitlements = [],
    filterOutSubscriptionGroupIds = [],
    subscriptionGroups = [],
  } = params;

  // We need to request the integration to potentially filter the product IDs (e.g. Cleeng localization IDs)
  const prefilteredUserProductIdentifiers = currentUserProductIdentifiers.map(
    (id) => transformProductIdentifierSafely(id)
  );

  // prepare the filter functions in an array, so adding new filters won't increase cyclomatic complexity
  const filterFunctions: FilterFunction[] = [
    // 1: it doesn't grant any of the entitlement tags the user needs to play a video.
    (info) => doesntGrantTagsNeededForVideo(info, fetchWithEntitlementTags),
    // 2: it doesn't grant any additional entitlement tags to the user.
    (info) => doesntGrantNewTagsForUser(info, filterOutMatchingEntitlements),
    // 3: the current subscription is not changeable to this product, because is not in the same group.
    (info) => isOutsideOfSubscriptionGroups(info, subscriptionGroups),
    // 4: it is not listed in the pre-filtered products array from Core
    (info) => isFilteredByCore(info, prefilteredProductIdentifiers),
    // 5: the user already owns this product
    (info) => isAlreadyOwnedProduct(info, prefilteredUserProductIdentifiers),
    // 6: the user already owns this subscription group
    (info) => isAlreadyOwnedGroup(info, filterOutSubscriptionGroupIds),
    // 7: subscription group owned on other platform
    (info) => isGroupOwnedOnOtherPlatform(info, otherPlatformGroupIds),
  ];

  // util for filtering by finding the first filter function that returns true. if none does, we can keep the product.
  const filterByAllFilterFunctions = (product: ProductInfo) =>
    !filterFunctions.some((filterFunction) => {
      const { result, message, after } = filterFunction(product);

      if (result) {
        debugLog(message);

        if (after === 'setOtherPlatformErrorGroupId') {
          functionResult.otherPlatformErrorGroupId = product.groupId;
        }
      }

      return result;
    });

  // filter out products by running through the filter functions. If any of them returns true, the product is not purchasable and we exclude it from the final list, and run the "after" action if needed.
  const { productIdentifiers, productInfoMap } = productInfos
    .filter(filterByAllFilterFunctions)
    .reduce<FilteredProductResult>(
      (acc, product) => {
        acc.productIdentifiers.push(product.productIdentifier);
        acc.productInfoMap[product.productIdentifier] = product;

        return acc;
      },
      { productIdentifiers: [], productInfoMap: {} }
    );

  // the remaining products are purchasable, so we return their identifiers in the result.
  functionResult.productIdentifiers = productIdentifiers;

  // we also return the product info map for the purchasable products, as we will need it in the UI and it's more efficient to pass it along than to look it up again based on the identifiers.
  functionResult.productInfoMap = productInfoMap;

  return functionResult;
};
